import { useState } from "react";
import { toast } from "react-toastify";
import {
  saveGiveawayToFirebase,
  updateGiveawayOnFirebase,
} from "../../../../../services/firebaseService/endPoints/admin/content/giveaway";
import logger from "../../../../../utils/logger";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../../store/adminData/adminData";
import { useNavigate } from "react-router-dom";
import useDashboardInfo from "../../../../../hooks/useDashboardInfo";
import { verifyStripeAccountId } from "../../../../../utils/errors";

export const useGiveawayForm = (form, selectedGroups) => {
  const { isParent } = useDashboardInfo();
  const navigate = useNavigate();
  const groupData = useAdminDataStore(selectCurrentGroupData);

  const [submitLoading, setSubmitLoading] = useState({
    save: false,
    publish: false,
    update: false,
  });
  const [stripeError, setStripeError] = useState(null);

  const validatePriceOptions = (priceOptions, form) => {
    if (priceOptions[0].price === null) {
      form.setFields([
        {
          name: ["priceOptions", "price0"],
          errors: ["Please enter a price for the first option"],
        },
      ]);
      return false;
    }

    if (priceOptions[0].entry === null) {
      form.setFields([
        {
          name: ["priceOptions", "entry0"],
          errors: ["Please enter a number of entries for the first option"],
        },
      ]);
      return false;
    }

    return true;
  };

  const handleSubmit = async (
    values,
    actionType,
    priceOptions,
    itemImageFile,
    donatedByImageFile,
    giveawayId,
  ) => {
    try {
      setSubmitLoading({ ...submitLoading, [actionType]: true });

      setStripeError(null);

      if (
        values.offerPurchase &&
        !verifyStripeAccountId(groupData?.paymentDetails?.stripeAccountId)
      ) {
        setStripeError(
          "Stripe account required to offer purchase of drawing entries.",
        );
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      if (!values.termsAndConditions) {
        toast.error("Please accept terms and conditions");
        return;
      }

      if (!validatePriceOptions(priceOptions, form)) {
        return;
      }

      const priceOptionsArray = priceOptions.map((option) => ({
        price: option.price ? option.price.toString() : "",
        entry: option.entry ? option.entry.toString() : "",
      }));

      const finalData = {
        ...values,
        priceOptions: priceOptionsArray,
      };

      // Add groups field if clubadmin
      if (isParent && selectedGroups?.selectionType) {
        finalData.audience = {
          selectionType: selectedGroups.selectionType,
          selectedValues: selectedGroups.selectedValues,
          groupIds: selectedGroups.groupIds,
        };

        // Add groups array based on selection type
        if (selectedGroups.selectionType === "all") {
          finalData.groups = ["global"];
        } else if (selectedGroups.selectionType === "official") {
          finalData.groups = ["Official Groups"];
        } else {
          // For region or specific groups, use the groupIds
          finalData.groups = selectedGroups.groupIds || [];
        }
      }

      const isLive = actionType === "publish";

      if (giveawayId) {
        await updateGiveawayOnFirebase(
          groupData,
          isLive,
          giveawayId,
          itemImageFile,
          donatedByImageFile,
          finalData,
          isParent,
        );
      } else {
        await saveGiveawayToFirebase(
          groupData,
          isLive,
          itemImageFile,
          donatedByImageFile,
          finalData,
          isParent,
        );
      }

      if (isParent) {
        navigate("/clubadmin/giveaways");
      } else {
        navigate("/admin/content/giveaways");
      }
    } catch (error) {
      logger.log("Failed to save giveaway", error);
    } finally {
      setSubmitLoading(false);
    }
  };

  return {
    submitLoading,
    handleSubmit,
    stripeError,
  };
};
